import React from 'react';
import { Button } from '../ui/button';
import axios from 'axios';
import {
  Link2, Loader2, CheckCircle2, XCircle, Store, Unlink,
  ExternalLink, AlertTriangle, Pin
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ProfileIntegrationsTab = ({
  shopifyStatus, shopifyExports, shopifyConnectError, shopifyMessage,
  handleShopifyDisconnect, handleShopifyClaim,
  pinterestStatus, pinterestConnecting, pinterestMessage,
  setPinterestStatus, setPinterestMessage, setPinterestConnecting,
  refreshShopifyBilling,
}) => {
  const handlePinterestConnect = async () => {
    setPinterestConnecting(true);
    try {
      const res = await axios.get(`${API}/pinterest/auth`);
      if (res.data.oauth_url) {
        window.location.href = res.data.oauth_url;
        return;
      }
    } catch (err) {
      const detail = err.response?.data?.detail || 'Connection error';
      setPinterestMessage({ type: 'error', text: detail });
      setTimeout(() => setPinterestMessage(null), 5000);
    }
    setPinterestConnecting(false);
  };

  const handlePinterestDisconnect = async () => {
    try {
      await axios.post(`${API}/pinterest/disconnect`);
      setPinterestStatus({ ...pinterestStatus, connected: false, username: '' });
      setPinterestMessage({ type: 'success', text: 'Pinterest disconnected.' });
      setTimeout(() => setPinterestMessage(null), 5000);
    } catch (err) { console.error(err); }
  };

  return (
    <div className="space-y-4" data-testid="integrations-section">
      {/* ─── Shopify ─── */}
      <div className="bg-white dark:bg-stone-900 rounded-2xl border border-stone-100 dark:border-stone-800 p-6" data-testid="shopify-integration-card">
        {shopifyMessage && (
          <div className={`flex items-start gap-2 p-3 rounded-xl mb-4 ${
            shopifyMessage.type === 'success'
              ? 'bg-green-50 dark:bg-green-900/20 border border-green-100 dark:border-green-800/30'
              : 'bg-red-50 dark:bg-red-900/20 border border-red-100 dark:border-red-800/30'
          }`} data-testid="shopify-message">
            {shopifyMessage.type === 'success'
              ? <CheckCircle2 className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" />
              : <XCircle className="w-4 h-4 text-red-500 mt-0.5 flex-shrink-0" />}
            <p className={`font-sans text-xs font-medium ${
              shopifyMessage.type === 'success' ? 'text-green-700 dark:text-green-400' : 'text-red-700 dark:text-red-400'
            }`}>{shopifyMessage.text}</p>
          </div>
        )}

        <div className="flex items-center gap-3 mb-5">
          <div className="w-10 h-10 rounded-xl bg-[#95BF47]/10 flex items-center justify-center"><Store className="w-5 h-5 text-[#95BF47]" /></div>
          <div>
            <h3 className="font-sans font-bold text-stone-800 dark:text-stone-200 text-sm">Shopify {shopifyStatus.connected && <span className="text-xs font-normal text-green-600 bg-green-50 px-2 py-0.5 rounded-full ml-1.5">Connected</span>}</h3>
            <p className="font-sans text-stone-400 dark:text-stone-500 text-xs">Export products directly to your store</p>
          </div>
        </div>

        {shopifyStatus.connected ? (
          <div>
            <div className="flex items-center justify-between p-3 bg-green-50/50 dark:bg-green-900/10 border border-green-100 dark:border-green-800/30 rounded-xl mb-4">
              <div className="flex items-center gap-2">
                <CheckCircle2 className="w-4 h-4 text-green-500" />
                <div>
                  <p className="font-sans font-semibold text-stone-800 dark:text-stone-200 text-xs" data-testid="shopify-shop-name">{shopifyStatus.shop_name || shopifyStatus.shop_domain}</p>
                  <p className="font-sans text-stone-400 dark:text-stone-500 text-[10px]">{shopifyStatus.shop_domain}</p>
                </div>
              </div>
              <button data-testid="disconnect-shopify-btn" onClick={handleShopifyDisconnect} className="flex items-center gap-1 text-[10px] font-sans text-stone-400 hover:text-red-500 transition-colors"><Unlink className="w-3 h-3" /> Disconnect</button>
            </div>
            {shopifyExports.length > 0 && (
              <div>
                <p className="font-sans font-bold text-stone-600 dark:text-stone-400 text-xs mb-2">Exported products ({shopifyExports.length})</p>
                <div className="space-y-2 max-h-48 overflow-y-auto" data-testid="shopify-exports-list">
                  {shopifyExports.map((exp) => (
                    <div key={exp.id} className="flex items-center justify-between p-2 rounded-lg bg-stone-50 dark:bg-stone-800">
                      <div className="min-w-0 flex-1">
                        <p className="font-sans font-medium text-stone-700 dark:text-stone-300 text-xs truncate">{exp.title}</p>
                        <p className="font-sans text-stone-400 dark:text-stone-500 text-[10px]">{new Date(exp.exported_at).toLocaleDateString()} — {exp.status}</p>
                      </div>
                      {exp.shopify_url && <a href={exp.shopify_url} target="_blank" rel="noopener noreferrer" className="p-1.5 rounded-lg hover:bg-stone-200 dark:hover:bg-stone-700 text-stone-400 flex-shrink-0"><ExternalLink className="w-3.5 h-3.5" /></a>}
                    </div>
                  ))}
                </div>
              </div>
            )}
            <div className="mt-4 pt-4 border-t border-stone-100 dark:border-stone-800">
              <p className="font-sans text-xs text-stone-500 dark:text-stone-400">
                Go to <strong>Generator</strong> to export products directly to your store.
              </p>
            </div>
          </div>
        ) : (
          <div className="space-y-4">
            {shopifyConnectError && (
              <div className="flex items-start gap-2 bg-red-50 dark:bg-red-900/20 border border-red-100 dark:border-red-800/30 rounded-xl p-3" data-testid="shopify-connect-error">
                <AlertTriangle className="w-4 h-4 text-red-500 mt-0.5 flex-shrink-0" />
                <p className="font-sans text-xs text-red-700 dark:text-red-400">{shopifyConnectError}</p>
              </div>
            )}
            {!shopifyStatus.configured && (
              <div className="flex items-start gap-2 bg-amber-50 dark:bg-amber-900/20 border border-amber-100 dark:border-amber-800/30 rounded-xl p-3">
                <AlertTriangle className="w-4 h-4 text-amber-500 mt-0.5 flex-shrink-0" />
                <p className="font-sans text-xs text-amber-700 dark:text-amber-400">
                  <span className="font-semibold">Not configured:</span> Shopify API keys are not set up yet.
                </p>
              </div>
            )}
            {shopifyStatus.pending_shop && (
              <div className="space-y-3">
                <div className="flex items-start gap-2 bg-blue-50 dark:bg-blue-900/20 border border-blue-100 dark:border-blue-800/30 rounded-xl p-3">
                  <Store className="w-4 h-4 text-blue-500 mt-0.5 flex-shrink-0" />
                  <p className="font-sans text-xs text-blue-700 dark:text-blue-400">
                    <span className="font-semibold">{shopifyStatus.pending_shop}</span> installed from Shopify App Store. Click below to link it to your account.
                  </p>
                </div>
                <Button data-testid="claim-shopify-btn" onClick={handleShopifyClaim}
                  className="w-full bg-[#5E8E3E] hover:bg-[#4B7232] text-white rounded-xl py-5 font-sans font-semibold">
                  <Link2 className="w-4 h-4 mr-2" /> Link Shopify Store
                </Button>
              </div>
            )}
            {!shopifyStatus.pending_shop && (
              <div className="space-y-3">
                <p className="font-sans text-xs text-stone-500 dark:text-stone-400">
                  Shopify integration is coming soon. You will be able to export products directly to your Shopify store.
                </p>
                <div
                  data-testid="install-shopify-btn"
                  className="flex items-center justify-center gap-2 w-full bg-stone-200 text-stone-400 rounded-xl py-3 font-sans font-semibold text-sm cursor-default"
                >
                  <Store className="w-4 h-4" /> Install from Shopify App Store — Coming soon
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {/* ─── Pinterest ─── */}
      <div className="bg-white dark:bg-stone-900 rounded-2xl border border-stone-100 dark:border-stone-800 p-6" data-testid="pinterest-integration-card">
        {pinterestMessage && (
          <div className={`flex items-start gap-2 p-3 rounded-xl mb-4 ${
            pinterestMessage.type === 'success'
              ? 'bg-green-50 dark:bg-green-900/20 border border-green-100 dark:border-green-800/30'
              : 'bg-red-50 dark:bg-red-900/20 border border-red-100 dark:border-red-800/30'
          }`} data-testid="pinterest-message">
            {pinterestMessage.type === 'success'
              ? <CheckCircle2 className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" />
              : <XCircle className="w-4 h-4 text-red-500 mt-0.5 flex-shrink-0" />}
            <p className={`font-sans text-xs font-medium ${
              pinterestMessage.type === 'success' ? 'text-green-700 dark:text-green-400' : 'text-red-700 dark:text-red-400'
            }`}>{pinterestMessage.text}</p>
          </div>
        )}

        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-[#E60023]/10 flex items-center justify-center"><Pin className="w-5 h-5 text-[#E60023]" /></div>
            <div>
              <h3 className="font-sans font-bold text-stone-800 dark:text-stone-200 text-sm">Pinterest</h3>
              {pinterestStatus.connected ? (
                <p className="font-sans text-green-600 dark:text-green-400 text-xs flex items-center gap-1"><CheckCircle2 className="w-3 h-3" /> @{pinterestStatus.username}</p>
              ) : (
                <p className="font-sans text-stone-400 dark:text-stone-500 text-xs">{pinterestStatus.configured ? 'Not connected' : 'API not configured'}</p>
              )}
            </div>
          </div>
          {pinterestStatus.connected ? (
            <button data-testid="disconnect-pinterest-profile-btn" onClick={handlePinterestDisconnect}
              className="flex items-center gap-1 px-3 py-1.5 rounded-lg text-xs font-sans font-medium text-stone-400 hover:text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors">
              <Unlink className="w-3.5 h-3.5" /> Disconnect
            </button>
          ) : (
            <Button data-testid="connect-pinterest-profile-btn" onClick={handlePinterestConnect} disabled={pinterestConnecting || !pinterestStatus.configured}
              className="bg-[#E60023] hover:bg-[#cc001f] text-white rounded-xl px-4 py-2 text-xs font-sans font-semibold disabled:opacity-50">
              {pinterestConnecting ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1.5" /> : <Link2 className="w-3.5 h-3.5 mr-1.5" />}
              Connect
            </Button>
          )}
        </div>

        {pinterestStatus.connected && (
          <div className="mt-4 pt-4 border-t border-stone-100 dark:border-stone-800">
            <p className="font-sans text-xs text-stone-500 dark:text-stone-400">
              Connected {pinterestStatus.connected_at ? new Date(pinterestStatus.connected_at).toLocaleDateString('en-US') : ''}.
              Go to <strong>Generator → Pins tab</strong> to publish and schedule pins.
            </p>
          </div>
        )}

        {!pinterestStatus.configured && (
          <div className="mt-4 pt-4 border-t border-stone-100 dark:border-stone-800">
            <p className="font-sans text-xs text-stone-400 dark:text-stone-500 leading-relaxed">
              Pinterest API is not configured yet. Add PINTEREST_APP_ID and PINTEREST_APP_SECRET in settings.
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export { ProfileIntegrationsTab };
